import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  TextField,
  MenuItem,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  Modal,
  Icon,
  Paper,
} from "@mui/material";
import { Place, ArrowBack } from "@mui/icons-material";
import axios from "axios";
import moment from "moment";
import "moment/locale/id";
import { useGeolocated } from "react-geolocated";
import { API_URL } from "../api";
import "../css/FormAbsensi.css";
import { CustomModal } from "../components/CustomModal";

export default function FormAbsensiSurveyor() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nik: "",
    nama: "",
    jabatan: "",
    minePermit: "",
    lokasi: "",
    jamTidur: "",
    keteranganSehat: "",
    konsumsiObat: "",
    latitude: "",
    longitude: "",
  });
  const [loading, setLoading] = useState(false);
  const [isWithinRadius, setIsWithinRadius] = useState(false);
  const [distance, setDistance] = useState(null);
  const [nearestLocation, setNearestLocation] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: "",
    message: "",
    type: "info",
    buttons: [],
  });

  const showModal = (title, message, type = "info", buttons = []) => {
    setModalConfig({ title, message, type, buttons });
    setModalVisible(true);
  };

  const ABSENSI_POINTS = [
    {
      name: "Pit Utara",
      center: { latitude: 1.168454252030331, longitude: 117.6982279529993 },
      radius: 300,
    },
    {
      name: "Pit Selatan",
      center: { latitude: 1.079950463339239, longitude: 117.69608531986545 },
      radius: 300,
    },
    {
      name: "Head Office GAM",
      center: { latitude: 1.143814966910681, longitude: 117.86989596120469 },
      radius: 300,
    },
  ];

  const { coords, isGeolocationAvailable, isGeolocationEnabled, getPosition } =
    useGeolocated({
      positionOptions: {
        enableHighAccuracy: true,
      },
      userDecisionTimeout: 5000,
      watchPosition: false,
    });

  // Fungsi untuk memicu pengambilan lokasi
  const handleGetLocation = () => {
    if (!isGeolocationAvailable || !isGeolocationEnabled) {
      showModal(
        "Lokasi Tidak Aktif",
        "Harap aktifkan layanan lokasi di browser atau perangkat Anda.",
        "error"
      );
      return;
    }
    getPosition();
  };
  useEffect(() => {
    const fetchUserData = () => {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);

          setFormData((prev) => ({
            ...prev,
            nama: userData.nama || "",
            nik: userData.nik || "",
            minePermit: userData.mine_permit || "",
          }));
        } catch (error) {
          console.error("Gagal mem-parsing data pengguna:", error);
        }
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    if (coords) {
      setFormData((prev) => ({
        ...prev,
        latitude: coords.latitude.toString(),
        longitude: coords.longitude.toString(),
      }));
      checkLocationRadius(coords.latitude, coords.longitude);
    }
  }, [coords]);

  const checkLocationRadius = (lat, lng) => {
    let withinRadius = false;
    let minDistance = Infinity;
    let nearestPoint = null;

    ABSENSI_POINTS.forEach((point) => {
      const distance = calculateDistance(
        lat,
        lng,
        point.center.latitude,
        point.center.longitude
      );

      if (distance < minDistance) {
        minDistance = distance;
        nearestPoint = point.name;
      }

      if (distance <= point.radius) {
        withinRadius = true;
      }
    });

    setDistance(minDistance);
    setNearestLocation(nearestPoint);
    setIsWithinRadius(withinRadius);

    if (!withinRadius) {
      showModal(
        "Peringatan",
        `Anda berada di luar radius absensi yang diizinkan (${Math.round(
          minDistance
        )}m dari titik terdekat)`
      );
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Radius bumi dalam meter
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    // Validasi radius
    if (!isWithinRadius && distance !== null) {
      // Hanya validasi jika jarak sudah dihitung
      showModal(
        "Gagal Absensi",
        "Anda harus berada dalam radius 1 km dari titik absensi yang ditentukan",
        "error"
      );
      return false;
    }

    // Validasi field wajib
    const requiredFields = [
      "jabatan",
      "lokasi",
      "jamTidur",
      "keteranganSehat",
      "konsumsiObat",
      "latitude",
      "longitude",
    ];
    const emptyFields = requiredFields.filter((field) => !formData[field]);

    if (emptyFields.length > 0) {
      showModal("Data Belum Lengkap", "Semua kolom wajib diisi", "warning");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/surveyor/absensi`, {
        user_id: formData.nik,
        nama_surveyor: formData.nama,
        jabatan: formData.jabatan,
        lokasi: formData.lokasi,
        no_mine_permit: formData.minePermit,
        jam_tidur: formData.jamTidur,
        keterangan_sehat: formData.keteranganSehat,
        konsumsi_obat: formData.konsumsiObat,
        latitude: formData.latitude,
        longitude: formData.longitude,
      });

      showModal("Berhasil", "Absensi P5M berhasil!", "success", [
        { text: "OK", onPress: () => window.history.back() },
      ]);
    } catch (error) {
      showModal("Gagal", error.response?.data?.message || "Terjadi kesalahan");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <header className="app-header">
        <div className="header-content">
          <button className="back-button" onClick={() => navigate(-1)}>
            <Icon>arrow_back</Icon>
          </button>
          <h1 className="header-title"> Absensi Hadir P5M</h1>
          <div className="header-spacer"></div>
        </div>
      </header>
      <Box sx={{ p: 3 }}>
        <Paper sx={{ mb: 3 }}>
          <TextField
            label="Nama"
            fullWidth
            margin="normal"
            value={formData.nama}
            disabled
          />

          <TextField
            select
            label="Jabatan"
            fullWidth
            margin="normal"
            name="jabatan"
            value={formData.jabatan}
            onChange={handleChange}
          >
            {["Superintendent", "Supervisor", "Foreman", "Helper Survey"].map(
              (option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              )
            )}
          </TextField>

          <TextField
            select
            label="Tim"
            fullWidth
            margin="normal"
            name="lokasi"
            value={formData.lokasi}
            onChange={handleChange}
          >
            {[
              { label: "Pit Utara", value: "Pit Utara, KM 22" },
              { label: "Pit Selatan", value: "Pit Selatan, Office 33" },
              { label: "Tim Drone", value: "Drone, Head Office" },
            ].map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="No. Mine Permit"
            fullWidth
            margin="normal"
            name="minePermit"
            value={formData.minePermit}
            disabled
          />
          <TextField
            label="Jumlah Jam Tidur"
            fullWidth
            margin="normal"
            name="jamTidur"
            value={formData.jamTidur}
            onChange={handleChange}
          />
          <TextField
            select
            label="Keterangan Sehat"
            fullWidth
            margin="normal"
            name="keteranganSehat"
            value={formData.keteranganSehat}
            onChange={handleChange}
          >
            {["Iya", "Tidak"].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Konsumsi Obat"
            fullWidth
            margin="normal"
            name="konsumsiObat"
            value={formData.konsumsiObat}
            onChange={handleChange}
          >
            {["Iya", "Tidak"].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
            <TextField
              label="Latitude"
              fullWidth
              margin="normal"
              value={formData.latitude}
              disabled
            />
            <IconButton
              color="primary"
              onClick={handleGetLocation}
              sx={{ ml: 1 }}
              size="large"
            >
              <Place />
            </IconButton>
          </Box>
          <TextField
            label="Longitude"
            fullWidth
            margin="normal"
            value={formData.longitude}
            disabled
          />

          {distance !== null && (
            <Typography color={isWithinRadius ? "success.main" : "error.main"}>
              Jarak: {Math.round(distance)}m ({nearestLocation}) -
              {isWithinRadius ? " Dalam radius" : " Di luar radius"}
            </Typography>
          )}
        </Paper>

        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleSubmit}
          disabled={loading || !isWithinRadius}
        >
          {loading ? <CircularProgress size={24} /> : "KIRIM"}
        </Button>

        {/* Modal */}
        <CustomModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          title={modalConfig.title}
          message={modalConfig.message}
          type={modalConfig.type}
          buttons={modalConfig.buttons}
        />
      </Box>
    </div>
  );
}
